<!-- DashboardFatturato.vue - Componente principale -->
<template>
    <v-container fluid class="ma-0 pa-0">
        <!-- Header con KPI principali -->
        <v-row class="ma-0 pa-2 mb-3">
            <v-col cols="12" md="3" v-for="(kpi, i) in schede" :key="i">
                <v-card class="kpi-card elevation-2">
                    <v-card-text class="pa-3">
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="text-caption text--secondary">
                                    {{ kpi.etichetta }}
                                </div>

                                <div class="text-h5 font-weight-bold">
                                    {{ kpi.valore }}
                                </div>

                                <div class="text-caption d-flex align-center"
                                    :class="kpi.tendenza > 0 ? 'success--text' : 'error--text'">
                                    <v-icon small class="mr-1">
                                        {{ kpi.tendenza > 0 ? 'trending_up' : 'trending_down' }}
                                    </v-icon>
                                    {{ Math.abs(kpi.tendenza) }}% {{ etichettaConfronto }}
                                </div>
                            </div>

                            <v-icon size="40" :color="kpi.accentColor">
                                {{ kpi.icona }}
                            </v-icon>
                        </div>
                    </v-card-text>
                </v-card>

            </v-col>
        </v-row>

        <!-- Tabs per diverse visualizzazioni -->
        <v-tabs v-model="tabAttiva" background-color="primary" dark>
            <v-tab>
                <v-icon left>show_chart</v-icon>
                Andamento
            </v-tab>
            <v-tab>
                <v-icon left>pie_chart</v-icon>
                Composizione
            </v-tab>
            <v-tab>
                <v-icon left>analytics</v-icon>
                Analisi Clienti
            </v-tab>
            <v-tab>
                <v-icon left>compare_arrows</v-icon>
                Confronti
            </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tabAttiva" class="mt-3" touchless>
            <!-- Tab 1: Andamento -->
            <v-tab-item>
                <tab-andamento :filtri="filtri" :aggiornaDati="aggiornaDati" :versioneBudget="versioneBudget"
                    @caricaDati="caricaDatiKPI" />
            </v-tab-item>

            <!-- Tab 2: Composizione -->
            <v-tab-item>
                <tab-composizione :filtri="filtri" :aggiornaDati="aggiornaDati" />
            </v-tab-item>

            <!-- Tab 3: Analisi Clienti -->
            <v-tab-item>
                <tab-analisi-clienti :filtri="filtri" :aggiornaDati="aggiornaDati" />
            </v-tab-item>

            <!-- Tab 4: Confronti -->
            <v-tab-item>
                <tab-confronti :filtri="filtri" :aggiornaDati="aggiornaDati" @budgetSalvato="versioneBudget++" />
            </v-tab-item>
        </v-tabs-items>
    </v-container>
</template>

<script>
import { get_data } from "@/api/api.documenti.js";
import TabAndamento from "./TabAndamento.vue";
import TabComposizione from "./TabComposizione.vue";
import TabAnalisiClienti from "./TabAnalisiClienti.vue";
import TabConfronti from "./TabConfronti.vue";

export default {
    name: "DashboardFatturato",

    components: {
        TabAndamento,
        TabComposizione,
        TabAnalisiClienti,
        TabConfronti
    },

    props: {
        filtri: { type: Object, default: () => ({}) },
        aggiornaDati: { type: Number, default: 0 }
    },

    data: () => ({
        tabAttiva: 0,

        // true se l'anno è in corso: le tendenze sono calcolate a parità di periodo
        kpiParziale: false,

        // aumenta quando il budget viene salvato, per aggiornare il target nella tab Andamento
        versioneBudget: 0,

        schede: [
            {
                etichetta: 'Fatturato YTD',
                valore: '€ 0',
                tendenza: 0,
                icona: 'euro_symbol',
                accentColor: 'success'
            },
            {
                etichetta: 'Numero Fatture',
                valore: '0',
                tendenza: 0,
                icona: 'description',
                accentColor: 'info'
            },
            {
                etichetta: 'Valore Medio',
                valore: '€ 0',
                tendenza: 0,
                icona: 'analytics',
                accentColor: 'warning'
            },
            {
                etichetta: 'Clienti Attivi',
                valore: '0',
                tendenza: 0,
                icona: 'people',
                accentColor: 'primary'
            }
        ]
    }),

    computed: {
        etichettaConfronto() {
            const annoPrecedente = this.filtri.anno - 1;
            return this.kpiParziale ? `vs stesso periodo ${annoPrecedente}` : `vs ${annoPrecedente}`;
        }
    },

    methods: {
        async caricaDatiKPI() {
            try {
                let res = await get_data("fatture/kpi", this.filtri);
                if (res && res.data) {
                    const dati = res.data;

                    this.kpiParziale = dati.parziale;

                    this.schede[0].valore = '€ ' + this.$functions.numberWithCommas(dati.fatturatoTotale.toFixed(2));
                    this.schede[0].tendenza = dati.tendenzaFatturato || 0;

                    this.schede[1].valore = dati.numeroFatture.toString();
                    this.schede[1].tendenza = dati.tendenzaNumero || 0;

                    this.schede[2].valore = '€ ' + this.$functions.numberWithCommas(dati.valoreMedio.toFixed(2));
                    this.schede[2].tendenza = dati.tendenzaValore || 0;

                    this.schede[3].valore = dati.clientiAttivi.toString();
                    this.schede[3].tendenza = dati.tendenzaClienti || 0;
                }
            } catch (error) {
                console.error("Errore caricamento KPI:", error);
            }
        }
    },

    mounted() {
        this.caricaDatiKPI();
    },

    watch: {
        aggiornaDati() {
            this.caricaDatiKPI();
        }
    }
}
</script>

<style scoped>
.kpi-card {
    border-left: 5px solid;
    background-color: var(--v-background-base);
}

.theme--light .kpi-card {
    background-color: #ffffff;
}

.theme--dark .kpi-card {
    background-color: #1e1e1e;
}
</style>